import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Play, 
  Heart, 
  Clock, 
  Trash2,
  Mic
} from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryPage = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoritePodcasts, setFavoritePodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [podcastsRes, favoritesRes] = await Promise.all([
          axios.get(`${API}/podcasts`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/favorites`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setPodcasts(podcastsRes.data.podcasts);
        setFavorites(favoritesRes.data.favorites);
        setFavoritePodcasts(favoritesRes.data.podcasts);
      } catch (error) {
        toast.error('Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const removeFavorite = async (podcastId) => {
    try {
      await axios.delete(`${API}/favorites/${podcastId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(prev => prev.filter(f => f.podcast_id !== podcastId));
      setFavoritePodcasts(prev => prev.filter(p => p.podcast_id !== podcastId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  const isFavorited = (podcastId) => {
    return favorites.some(f => f.podcast_id === podcastId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0B' }}>
        <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const PodcastCard = ({ podcast, showFavoriteAction = false }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel rounded-xl p-4 hover:border-purple-500/30 transition-colors cursor-pointer"
      onClick={() => navigate(`/player/${podcast.podcast_id}`)}
      data-testid={`podcast-card-${podcast.podcast_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(109, 40, 217, 0.2)' }}
          >
            <Mic className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 
              className="font-medium text-sm mb-0.5"
              style={{ color: '#F8FAFC' }}
            >
              {podcast.topic}
            </h3>
            <p className="text-xs" style={{ color: '#64748B' }}>
              {podcast.duration} min • {podcast.experts.length} expert{podcast.experts.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {showFavoriteAction ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                removeFavorite(podcast.podcast_id);
              }}
              className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-white/5"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          ) : isFavorited(podcast.podcast_id) && (
            <Heart className="w-4 h-4 text-red-500 fill-current" />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-purple-400 hover:text-purple-300 hover:bg-white/5"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/player/${podcast.podcast_id}`);
            }}
          >
            <Play className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs" style={{ color: '#64748B' }}>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDate(podcast.created_at)}
        </div>
        <div 
          className={`px-2 py-0.5 rounded-full text-xs ${
            podcast.status === 'ready' 
              ? 'bg-green-500/20 text-green-400'
              : podcast.status === 'failed' || podcast.status === 'audio_failed'
              ? 'bg-red-500/20 text-red-400'
              : 'bg-yellow-500/20 text-yellow-400'
          }`}
        >
          {podcast.status === 'ready' ? 'Ready' : 
           podcast.status === 'failed' ? 'Failed' :
           podcast.status === 'audio_failed' ? 'Audio Failed' :
           'Processing'}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0B' }}>
      {/* Header */}
      <header className="border-b px-6 py-4" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </Button>

          <h1 
            className="text-xl font-medium"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
          >
            Library
          </h1>

          <div className="w-20" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <Tabs defaultValue="history" className="w-full">
          <TabsList className="w-full justify-start mb-8 bg-transparent border-b rounded-none" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <TabsTrigger 
              value="history" 
              className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-purple-500 rounded-none px-6 py-3 text-gray-400"
              data-testid="history-tab"
            >
              <Clock className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger 
              value="favorites"
              className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-purple-500 rounded-none px-6 py-3 text-gray-400"
              data-testid="favorites-tab"
            >
              <Heart className="w-4 h-4 mr-2" />
              Favorites
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history">
            {podcasts.length === 0 ? (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium mb-2" style={{ color: '#F8FAFC' }}>
                  No podcasts yet
                </h3>
                <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
                  Generate your first AI podcast to see it here
                </p>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-purple-600 hover:bg-purple-500"
                >
                  Create Podcast
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {podcasts.map((podcast) => (
                  <PodcastCard key={podcast.podcast_id} podcast={podcast} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="favorites">
            {favoritePodcasts.length === 0 ? (
              <div className="text-center py-16">
                <Heart className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium mb-2" style={{ color: '#F8FAFC' }}>
                  No favorites yet
                </h3>
                <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
                  Heart a podcast to save it here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {favoritePodcasts.map((podcast) => (
                  <PodcastCard 
                    key={podcast.podcast_id} 
                    podcast={podcast} 
                    showFavoriteAction 
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default HistoryPage;
